'use client';

import { useRef, useState } from 'react';
import { useTaskAttachments, useUploadFile, useDeleteAttachment, downloadAttachment, formatFileSize } from '@/hooks/useFileUpload';
import { formatDistanceToNow } from 'date-fns';

interface FileUploadSectionProps {
  taskId: number;
}

export default function FileUploadSection({ taskId }: FileUploadSectionProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const { data: attachments = [], isLoading } = useTaskAttachments(taskId);
  const uploadFile = useUploadFile();
  const deleteAttachment = useDeleteAttachment();

  const uploadFileWithValidation = (file: File) => {
    // Reset error state
    setUploadError(null);

    // Client-side validation
    const maxSize = 10 * 1024 * 1024; // 10 MB
    if (file.size > maxSize) {
      setUploadError('File size must be less than 10 MB');
      return;
    }

    // Upload the file
    uploadFile.mutate(
      { taskId, file },
      {
        onError: (error) => {
          setUploadError(error.message || 'Failed to upload file');
        },
        onSuccess: () => {
          // Clear the file input
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        },
      }
    );
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadFileWithValidation(file);
  };

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0]; // Only take the first file
      uploadFileWithValidation(file);
    }
  };

  const handleDownload = async (attachment: any) => {
    try {
      await downloadAttachment(attachment);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download file');
    }
  };

  const handleDeleteClick = (attachmentId: number) => {
    setDeleteConfirm(attachmentId);
  };

  const handleDeleteConfirm = (attachmentId: number) => {
    deleteAttachment.mutate(
      { attachmentId, taskId },
      {
        onSuccess: () => {
          setDeleteConfirm(null);
        },
      }
    );
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm(null);
  };

  const getFileIcon = (contentType: string) => {
    if (contentType.startsWith('image/')) return '🖼️';
    if (contentType.startsWith('video/')) return '🎥';
    if (contentType.includes('pdf')) return '📄';
    if (contentType.includes('word') || contentType.includes('document')) return '📝';
    if (contentType.includes('excel') || contentType.includes('spreadsheet')) return '📊';
    if (contentType.includes('powerpoint') || contentType.includes('presentation')) return '📽️';
    if (contentType.includes('zip') || contentType.includes('tar') || contentType.includes('gzip')) return '📦';
    return '📎';
  };

  return (
    <div className="space-y-4">
      {/* Drag and Drop Zone */}
      <div
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`relative border-2 border-dashed rounded-lg p-6 transition-all ${
          isDragging
            ? 'border-orange-500 bg-orange-500/10'
            : 'border-[#2d1b3d] bg-[#1a1f3a]/30 hover:border-gray-600'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileSelect}
          className="hidden"
        />

        <div className="flex flex-col items-center justify-center gap-3">
          <div className={`text-4xl transition-transform ${isDragging ? 'scale-110' : ''}`}>
            📎
          </div>
          <div className="text-center">
            <p className="text-gray-300 text-sm mb-1">
              {isDragging ? 'Drop file here' : 'Drag and drop a file here'}
            </p>
            <p className="text-gray-500 text-xs">or</p>
          </div>
          <button
            onClick={handleAttachClick}
            disabled={uploadFile.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#1a1f3a] border border-[#2d1b3d] rounded-lg text-white hover:bg-[#2d1b3d] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
              />
            </svg>
            {uploadFile.isPending ? 'Uploading...' : 'Browse Files'}
          </button>
          <p className="text-gray-500 text-xs">Max file size: 10 MB</p>
        </div>

        {uploadError && (
          <div className="text-red-400 text-sm text-center mt-3">{uploadError}</div>
        )}
      </div>

      {/* Attachments List */}
      <div className="space-y-2">
        {isLoading ? (
          <div className="text-gray-400 text-sm text-center py-4">Loading attachments...</div>
        ) : attachments.length === 0 ? (
          <div className="text-gray-400 text-sm text-center py-4">
            No attachments yet
          </div>
        ) : (
          attachments.map((attachment) => (
            <div
              key={attachment.id}
              className="flex items-center gap-3 p-3 bg-[#1a1f3a] border border-[#2d1b3d] rounded-lg hover:bg-[#2d1b3d] transition-colors"
            >
              {/* File Icon */}
              <div className="text-2xl flex-shrink-0">
                {getFileIcon(attachment.content_type)}
              </div>

              {/* File Info */}
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm font-medium truncate">
                  {attachment.original_filename}
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-400 mt-1">
                  <span>{formatFileSize(attachment.size)}</span>
                  <span>•</span>
                  <span>
                    {formatDistanceToNow(new Date(attachment.date_created), { addSuffix: true })}
                  </span>
                  {attachment.created_by && (
                    <>
                      <span>•</span>
                      <span>
                        {`${attachment.created_by.user.first_name || 'Unknown'} ${attachment.created_by.user.last_name || 'User'}`.trim()}
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Actions */}
              {deleteConfirm === attachment.id ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleDeleteConfirm(attachment.id)}
                    className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition-colors"
                  >
                    Confirm
                  </button>
                  <button
                    onClick={handleDeleteCancel}
                    className="px-2 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  {/* Download Button */}
                  <button
                    onClick={() => handleDownload(attachment)}
                    className="p-2 text-gray-400 hover:text-white transition-colors"
                    title="Download"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                  </button>

                  {/* Delete Button */}
                  <button
                    onClick={() => handleDeleteClick(attachment.id)}
                    className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                    title="Delete"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
